import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolErrorCode,
  type ToolOperation,
} from '@saas-pages/shared/public/runtime'
import { useLocale } from '@/shared/i18n'
import {
  DELAY_STEP_MS,
  FilePreflightError,
  MAX_AGGREGATE_PIXELS,
  MAX_CANVAS_SIDE,
  MAX_FRAME_PIXELS,
  MAX_FRAMES,
  MAX_REPEAT,
  MAX_SOURCE_BYTES,
  MIN_DELAY_MS,
  MIN_FRAMES,
  MIN_REPEAT,
  GifGuardrailError,
  effectiveDelay,
  inspectGifSource,
  moveFrame,
  previewDelayMs,
  validateGifDraft,
  type FitMode,
  type GifDraftFrame,
  type GifSettings,
  type LoopMode,
} from '@/features/image-gif-maker/model/gifMaker'
import {
  startGifGeneration,
  type GifGenerationFailure,
  type GifGenerationOwner,
} from '@/features/image-gif-maker/model/workerLifecycle'
import type { GifWorkerRequest } from '@/features/image-gif-maker/model/types'
import { ToolHero } from '@/shared/ui/ToolHero'

interface Asset extends GifDraftFrame {
  id: number
  url: string
}

interface GifResult {
  blob: Blob
  url: string
}

const ANALYTICS = {
  app: 'imgify',
  domain: 'imgify.ru',
  route: '/sozdat-gif-iz-foto',
  utility: 'image-gif-maker',
  processingMode: 'local',
  inputMethod: 'upload',
} satisfies ToolAnalyticsContext

const COPY = {
  ru: {
    eyebrow: 'GIF из фотографий',
    title: 'Создать GIF из фото онлайн',
    description: 'Расположите 2–20 статичных JPG, PNG или WebP, настройте время кадров и соберите GIF локально в браузере.',
    drop: 'Перетащите изображения сюда',
    dropHint: '2–20 JPG, PNG или WebP, до 15 МБ и 16 МП каждое',
    choose: 'Выбрать фото',
    order: 'Кадры и порядок',
    orderHint: 'Перетащите карточку или используйте кнопки «Выше» и «Ниже». Порядок предпросмотра совпадает с GIF.',
    moveUp: 'Выше',
    moveDown: 'Ниже',
    remove: 'Удалить',
    frameDelay: 'Задержка кадра',
    useGlobal: 'Общая',
    preview: 'Предпросмотр порядка и времени',
    previewAlt: 'Текущий кадр предпросмотра GIF',
    settings: 'Параметры GIF',
    width: 'Ширина',
    height: 'Высота',
    fit: 'Заполнение кадра',
    contain: 'Вписать целиком',
    cover: 'Заполнить с обрезкой',
    globalDelay: 'Общая задержка',
    loop: 'Повтор',
    once: 'Один раз',
    forever: 'Бесконечно',
    repeats: 'Заданное число',
    repeatCount: 'Число повторов',
    background: 'Фон',
    transparent: 'Прозрачный фон',
    transparencyWarning: 'GIF поддерживает только бинарную прозрачность: полупрозрачные края станут полностью прозрачными или непрозрачными.',
    paletteWarning: 'GIF ограничен палитрой до 255 цветов без дизеринга. На фотографиях возможны полосы и упрощение оттенков.',
    generate: 'Создать GIF',
    generating: 'Создаём GIF',
    progress: 'Готово кадров',
    cancel: 'Отменить',
    retry: 'Повторить',
    download: 'Скачать GIF',
    reset: 'Начать заново',
    actual: 'Готовый GIF',
    privacy: 'Проверка и preview остаются локальными. Генерация и кодирование выполняются в одном отдельном Worker; файлы не отправляются на сервер и не попадают в аналитику.',
    howToTitle: 'Как создать GIF из фото',
    howTo: [
      'Добавьте от 2 до 20 статичных изображений JPG, PNG или WebP.',
      'Расположите кадры, настройте общую или индивидуальную задержку и повтор.',
      'Выберите размер, вписывание или обрезку, затем нажмите «Создать GIF».',
      'Проверьте фактическую анимацию и скачайте готовый файл.',
    ],
    faqTitle: 'Вопросы о GIF',
    faq: [
      ['Фото отправляются на сервер?', 'Нет. Проверка, preview и кодирование выполняются локально в браузере.'],
      ['Почему оттенки могут измениться?', 'GIF хранит не более 256 цветов и бинарную прозрачность. Этот инструмент использует фиксированный предел 255 цветов без дизеринга.'],
      ['Можно добавить готовый GIF или видео?', 'Нет. Инструмент принимает только статичные JPG, PNG и WebP. Анимированные изображения, HEIC, архивы и видео отклоняются.'],
    ],
    errors: {
      needFrames: 'Добавьте минимум два кадра.',
      tooMany: 'Можно добавить не более 20 кадров.',
      unsupported: 'Поддерживаются только полные статичные JPG, PNG и WebP.',
      animated: 'Анимированные изображения не поддерживаются.',
      sourceLimit: 'Файл превышает 15 МБ или 16 МП.',
      decode: 'Не удалось безопасно открыть изображение.',
      draft: 'Проверьте размер холста, задержки и число кадров.',
      browser: 'Браузер не поддерживает необходимый Worker/OffscreenCanvas.',
      output: 'GIF превысил 32 МБ. Уменьшите холст, число кадров или используйте изображения с меньшим числом цветов.',
      timeout: 'Генерация заняла слишком много времени. Уменьшите холст или число кадров.',
      failed: 'Не удалось создать GIF. Исходники сохранены — можно повторить.',
      cancelled: 'Генерация отменена. Исходники сохранены.',
    },
    related: 'Связанные инструменты',
  },
  en: {
    eyebrow: 'GIF from photos',
    title: 'Create a GIF from photos online',
    description: 'Order 2–20 static JPG, PNG or WebP images, set frame timing and build a GIF locally in your browser.',
    drop: 'Drop images here',
    dropHint: '2–20 JPG, PNG or WebP files, up to 15 MB and 16 MP each',
    choose: 'Choose photos',
    order: 'Frames and order',
    orderHint: 'Drag a card or use Move up and Move down. Preview order matches the GIF.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    frameDelay: 'Frame delay',
    useGlobal: 'Global',
    preview: 'Order and timing preview',
    previewAlt: 'Current GIF preview frame',
    settings: 'GIF settings',
    width: 'Width',
    height: 'Height',
    fit: 'Frame fit',
    contain: 'Contain',
    cover: 'Cover with crop',
    globalDelay: 'Global delay',
    loop: 'Loop',
    once: 'Once',
    forever: 'Forever',
    repeats: 'Fixed repeats',
    repeatCount: 'Repeat count',
    background: 'Background',
    transparent: 'Transparent background',
    transparencyWarning: 'GIF only supports binary transparency: semi-transparent edges become fully transparent or opaque.',
    paletteWarning: 'GIF is limited to a 255-colour palette without dithering. Photos may show banding and simplified shades.',
    generate: 'Create GIF',
    generating: 'Creating GIF',
    progress: 'Frames complete',
    cancel: 'Cancel',
    retry: 'Retry',
    download: 'Download GIF',
    reset: 'Start over',
    actual: 'Generated GIF',
    privacy: 'Validation and preview stay local. Generation and encoding run in one dedicated Worker; files are not uploaded or included in analytics.',
    howToTitle: 'How to create a GIF from photos',
    howTo: [
      'Add 2–20 static JPG, PNG or WebP images.',
      'Order frames, choose a global or per-frame delay, and set looping.',
      'Choose the canvas and fit, then select Create GIF.',
      'Check the actual animation and download the result.',
    ],
    faqTitle: 'GIF questions',
    faq: [
      ['Are photos uploaded?', 'No. Validation, preview and encoding run locally in your browser.'],
      ['Why can colours change?', 'GIF stores no more than 256 colours and binary transparency. This tool uses a fixed 255-colour limit without dithering.'],
      ['Can I add an existing GIF or video?', 'No. Only static JPG, PNG and WebP are accepted. Animated images, HEIC, archives and video are rejected.'],
    ],
    errors: {
      needFrames: 'Add at least two frames.',
      tooMany: 'You can add at most 20 frames.',
      unsupported: 'Only complete static JPG, PNG and WebP files are supported.',
      animated: 'Animated images are not supported.',
      sourceLimit: 'A file exceeds 15 MB or 16 MP.',
      decode: 'The image could not be opened safely.',
      draft: 'Check the canvas size, delays and frame count.',
      browser: 'This browser lacks the required Worker/OffscreenCanvas support.',
      output: 'The GIF exceeded 32 MB. Reduce the canvas, use fewer frames, or use images with fewer colours.',
      timeout: 'Generation took too long. Reduce the canvas or frame count.',
      failed: 'The GIF could not be created. Sources are preserved for retry.',
      cancelled: 'Generation cancelled. Sources are preserved.',
    },
    related: 'Related tools',
  },
} as const

function analyticsError(code: GifGenerationFailure): ToolErrorCode {
  if (code === 'cancelled') return 'cancelled'
  if (code === 'timeout') return 'timeout'
  if (code === 'output_too_large') return 'size_limit'
  if (code === 'unsupported_browser') return 'unsupported_output'
  if (code === 'invalid_input') return 'invalid_input'
  return 'processing_failed'
}

export function ImageGifMakerTool() {
  const { locale } = useLocale()
  const copy = COPY[locale]
  const [assets, setAssets] = useState<Asset[]>([])
  const assetsRef = useRef<Asset[]>([])
  assetsRef.current = assets
  const inputRef = useRef<HTMLInputElement>(null)
  const nextIdRef = useRef(1)
  const loadGenerationRef = useRef(0)
  const generationRef = useRef(0)
  const ownerRef = useRef<GifGenerationOwner | null>(null)
  const operationRef = useRef<ToolOperation | null>(null)
  const resultUrlRef = useRef<string | null>(null)
  const [result, setResult] = useState<GifResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState({ completed: 0, total: 0 })
  const [error, setError] = useState<string | null>(null)
  const [dropActive, setDropActive] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)
  const [width, setWidth] = useState(640)
  const [height, setHeight] = useState(640)
  const [fit, setFit] = useState<FitMode>('contain')
  const [transparent, setTransparent] = useState(false)
  const [background, setBackground] = useState('#ffffff')
  const [globalDelayMs, setGlobalDelayMs] = useState(500)
  const [loopMode, setLoopMode] = useState<LoopMode>('forever')
  const [repeatCount, setRepeatCount] = useState(2)

  const settings = useMemo<GifSettings>(() => ({
    size: { width, height }, fit, transparent, background, globalDelayMs, loopMode, repeatCount,
  }), [background, fit, globalDelayMs, height, loopMode, repeatCount, transparent, width])

  const revokeResult = useCallback(() => {
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    resultUrlRef.current = null
    setResult(null)
  }, [])

  const invalidateGeneration = useCallback((reportCancellation: boolean) => {
    generationRef.current += 1
    if (ownerRef.current) {
      if (reportCancellation) ownerRef.current.cancel()
      else {
        ownerRef.current.cancel()
        operationRef.current = null
      }
    }
    ownerRef.current = null
    operationRef.current = null
    setBusy(false)
    setProgress({ completed: 0, total: 0 })
  }, [])

  useEffect(() => () => {
    loadGenerationRef.current += 1
    generationRef.current += 1
    ownerRef.current?.cancel()
    ownerRef.current = null
    for (const asset of assetsRef.current) URL.revokeObjectURL(asset.url)
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
  }, [])

  useEffect(() => {
    if (assets.length === 0) {
      setPreviewIndex(0)
      return
    }
    if (previewIndex >= assets.length) setPreviewIndex(0)
    const current = assets[previewIndex % assets.length]
    const requestedDelay = current ? effectiveDelay(current, globalDelayMs) : globalDelayMs
    const previewDelay = previewDelayMs(requestedDelay)
    const timer = window.setTimeout(
      () => setPreviewIndex((index) => (index + 1) % assets.length),
      previewDelay,
    )
    return () => window.clearTimeout(timer)
  }, [assets, globalDelayMs, previewIndex])

  const rejectFile = useCallback((errorValue: unknown) => {
    let message: string = copy.errors.unsupported
    let code: ToolErrorCode = 'unsupported_input'
    if (errorValue instanceof FilePreflightError) {
      if (errorValue.code === 'animated_unsupported') message = copy.errors.animated
      else if (errorValue.code === 'file_too_large' || errorValue.code === 'too_many_pixels') {
        message = copy.errors.sourceLimit
        code = 'size_limit'
      } else if (errorValue.code === 'decode_failed') {
        message = copy.errors.decode
        code = 'processing_failed'
      }
    }
    setError(message)
    trackToolRejection(ANALYTICS, code)
  }, [copy.errors])

  const addFiles = useCallback(async (list: FileList | File[]) => {
    const generation = ++loadGenerationRef.current
    setError(null)
    const files = Array.from(list)
    if (assetsRef.current.length + files.length > MAX_FRAMES) {
      setError(copy.errors.tooMany)
      trackToolRejection(ANALYTICS, 'size_limit')
      return
    }
    const accepted: Asset[] = []
    for (const file of files) {
      try {
        const meta = await inspectGifSource(file)
        if (generation !== loadGenerationRef.current) {
          for (const asset of accepted) URL.revokeObjectURL(asset.url)
          return
        }
        accepted.push({ id: nextIdRef.current++, file, meta, delayMs: null, url: URL.createObjectURL(file) })
      } catch (preflightError) {
        for (const asset of accepted) URL.revokeObjectURL(asset.url)
        if (generation === loadGenerationRef.current) rejectFile(preflightError)
        return
      }
    }
    if (generation !== loadGenerationRef.current) {
      for (const asset of accepted) URL.revokeObjectURL(asset.url)
      return
    }
    if (accepted.length > 0) {
      invalidateGeneration(false)
      revokeResult()
      setAssets((current) => [...current, ...accepted])
    }
  }, [copy.errors.tooMany, invalidateGeneration, rejectFile, revokeResult])

  const move = useCallback((from: number, to: number) => {
    setAssets((current) => moveFrame(current, from, to))
    setPreviewIndex(0)
    invalidateGeneration(false)
    revokeResult()
  }, [invalidateGeneration, revokeResult])

  const remove = useCallback((id: number) => {
    setAssets((current) => {
      const target = current.find((asset) => asset.id === id)
      if (target) URL.revokeObjectURL(target.url)
      return current.filter((asset) => asset.id !== id)
    })
    setPreviewIndex(0)
    invalidateGeneration(false)
    revokeResult()
  }, [invalidateGeneration, revokeResult])

  const updateDelay = useCallback((id: number, value: string) => {
    setAssets((current) => current.map((asset) => asset.id === id
      ? { ...asset, delayMs: value === '' ? null : Number(value) }
      : asset))
    invalidateGeneration(false)
    revokeResult()
  }, [invalidateGeneration, revokeResult])

  const reset = useCallback(() => {
    loadGenerationRef.current += 1
    invalidateGeneration(false)
    for (const asset of assetsRef.current) URL.revokeObjectURL(asset.url)
    setAssets([])
    setError(null)
    setPreviewIndex(0)
    revokeResult()
  }, [invalidateGeneration, revokeResult])

  const generate = useCallback(() => {
    setError(null)
    if (assets.length < MIN_FRAMES) {
      setError(copy.errors.needFrames)
      trackToolRejection(ANALYTICS, 'invalid_input')
      return
    }
    try {
      validateGifDraft(assets, settings)
    } catch (validationError) {
      setError(validationError instanceof GifGuardrailError ? copy.errors.draft : copy.errors.failed)
      trackToolRejection(ANALYTICS, validationError instanceof GifGuardrailError && !['invalid_delay', 'invalid_repeat', 'frame_count'].includes(validationError.code) ? 'size_limit' : 'invalid_input')
      return
    }
    if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') {
      setError(copy.errors.browser)
      trackToolRejection(ANALYTICS, 'unsupported_output')
      return
    }

    invalidateGeneration(false)
    revokeResult()
    const generation = generationRef.current
    const operation = startToolOperation(ANALYTICS)
    operationRef.current = operation
    setBusy(true)
    setProgress({ completed: 0, total: assets.length })
    const request: GifWorkerRequest = {
      frames: assets.map((asset) => ({
        file: asset.file,
        meta: asset.meta,
        delayMs: effectiveDelay(asset, globalDelayMs),
      })),
      width, height, fit, transparent, background, loopMode, repeatCount,
    }
    ownerRef.current = startGifGeneration(request, {
      progress: (completed, total) => {
        if (generationRef.current === generation) setProgress({ completed, total })
      },
      success: (blob, durationMs) => {
        if (generationRef.current !== generation) return
        ownerRef.current = null
        operationRef.current = null
        setBusy(false)
        const url = URL.createObjectURL(blob)
        resultUrlRef.current = url
        setResult({ blob, url })
        operation.success(durationMs)
      },
      error: (code) => {
        if (generationRef.current !== generation) return
        ownerRef.current = null
        operationRef.current = null
        setBusy(false)
        setError(code === 'output_too_large' ? copy.errors.output
          : code === 'timeout' ? copy.errors.timeout
            : code === 'cancelled' ? copy.errors.cancelled
              : code === 'unsupported_browser' ? copy.errors.browser
                : copy.errors.failed)
        operation.error(analyticsError(code))
      },
    })
  }, [assets, background, copy.errors, fit, globalDelayMs, height, invalidateGeneration, loopMode, repeatCount, revokeResult, settings, transparent, width])

  const cancel = useCallback(() => {
    ownerRef.current?.cancel()
  }, [])

  const download = useCallback(() => {
    if (!result) return
    const link = document.createElement('a')
    link.href = result.url
    link.download = 'imgify-animation.gif'
    link.click()
  }, [result])

  const previewAsset = assets[previewIndex % Math.max(1, assets.length)]
  const canvasPixels = width * height
  const aggregatePixels = canvasPixels * assets.length
  const draftWithinCanvasLimits = width > 0 && height > 0
    && Math.max(width, height) <= MAX_CANVAS_SIDE
    && canvasPixels <= MAX_FRAME_PIXELS
    && aggregatePixels <= MAX_AGGREGATE_PIXELS

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero eyebrow={copy.eyebrow} title={copy.title} description={copy.description} />

      <Card>
        <CardContent className="p-4 sm:p-6">
          <div
            className={cn('rounded-lg border-2 border-dashed p-5 text-center transition-colors', dropActive ? 'border-primary bg-primary/5' : 'border-border bg-muted/20')}
            onDragOver={(event) => { event.preventDefault(); setDropActive(true) }}
            onDragLeave={() => setDropActive(false)}
            onDrop={(event) => { event.preventDefault(); setDropActive(false); void addFiles(event.dataTransfer.files) }}
          >
            <p className="hidden font-medium [@media(pointer:fine)]:block">{copy.drop}</p>
            <p className="mt-1 text-sm text-muted-foreground">{copy.dropHint}</p>
            <input
              ref={inputRef}
              className="sr-only"
              type="file"
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              multiple
              onChange={(event) => { if (event.target.files) void addFiles(event.target.files); event.target.value = '' }}
            />
            <Button className="mt-4" type="button" onClick={() => inputRef.current?.click()} disabled={busy}>{copy.choose}</Button>
          </div>
          {error ? <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" role="alert">{error}</div> : null}
        </CardContent>
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <h2 className="text-xl font-semibold">{copy.preview}</h2>
              <div className="mx-auto mt-4 grid min-h-64 max-w-2xl place-items-center overflow-hidden rounded-lg border" style={{ aspectRatio: `${Math.max(1, width)} / ${Math.max(1, height)}`, background: transparent ? 'repeating-conic-gradient(#e5e7eb 0 25%, #fff 0 50%) 50% / 20px 20px' : background }}>
                {previewAsset ? <img className={cn('h-full w-full', fit === 'contain' ? 'object-contain' : 'object-cover')} src={previewAsset.url} alt={copy.previewAlt} /> : <p className="p-6 text-center text-sm text-muted-foreground">{copy.errors.needFrames}</p>}
              </div>
              {assets.length > 0 ? <p className="mt-3 text-center text-sm text-muted-foreground">{previewIndex + 1} / {assets.length} · {effectiveDelay(previewAsset!, globalDelayMs)} ms</p> : null}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <h2 className="text-xl font-semibold">{copy.order}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{copy.orderHint}</p>
              <ol className="mt-4 grid gap-3 sm:grid-cols-2">
                {assets.map((asset, index) => (
                  <li
                    key={asset.id}
                    draggable={!busy}
                    className="rounded-md border bg-background p-3"
                    onDragStart={(event) => event.dataTransfer.setData('text/plain', String(index))}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => { event.preventDefault(); const from = Number(event.dataTransfer.getData('text/plain')); if (Number.isInteger(from)) move(from, index) }}
                  >
                    <div className="flex items-center gap-3">
                      <img className="h-14 w-14 rounded object-cover" src={asset.url} alt="" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{index + 1}</p>
                        <p className="text-xs text-muted-foreground">{asset.meta.width} × {asset.meta.height}</p>
                      </div>
                    </div>
                    <label className="mt-3 block text-xs font-medium">
                      {copy.frameDelay}
                      <div className="mt-1 grid grid-cols-[auto_minmax(0,1fr)] gap-2">
                        <label className="flex items-center gap-1 text-xs font-normal">
                          <input type="checkbox" checked={asset.delayMs === null} onChange={(event) => updateDelay(asset.id, event.target.checked ? '' : String(globalDelayMs))} disabled={busy} />
                          {copy.useGlobal}
                        </label>
                        <input
                          className="w-full rounded-md border bg-background px-2 py-1.5"
                          aria-label={copy.frameDelay + ': ' + (index + 1)}
                          type="number"
                          min={MIN_DELAY_MS}
                          max={10000}
                          step={DELAY_STEP_MS}
                          value={asset.delayMs ?? globalDelayMs}
                          onChange={(event) => updateDelay(asset.id, event.target.value)}
                          disabled={busy || asset.delayMs === null}
                        />
                      </div>
                    </label>
                    <div className="mt-3 flex flex-wrap gap-1">
                      <Button size="sm" variant="outline" disabled={busy || index === 0} onClick={() => move(index, index - 1)} aria-label={`${copy.moveUp}: ${index + 1}`}>{copy.moveUp}</Button>
                      <Button size="sm" variant="outline" disabled={busy || index === assets.length - 1} onClick={() => move(index, index + 1)} aria-label={`${copy.moveDown}: ${index + 1}`}>{copy.moveDown}</Button>
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove(asset.id)}>{copy.remove}</Button>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        <Card className="h-fit">
          <CardContent className="space-y-5 p-4 sm:p-6">
            <h2 className="text-xl font-semibold">{copy.settings}</h2>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm font-medium">{copy.width}<input className="mt-1 w-full rounded-md border bg-background px-3 py-2" type="number" min="1" max={MAX_CANVAS_SIDE} value={width} onChange={(event) => { setWidth(Number(event.target.value)); invalidateGeneration(false); revokeResult() }} disabled={busy} /></label>
              <label className="text-sm font-medium">{copy.height}<input className="mt-1 w-full rounded-md border bg-background px-3 py-2" type="number" min="1" max={MAX_CANVAS_SIDE} value={height} onChange={(event) => { setHeight(Number(event.target.value)); invalidateGeneration(false); revokeResult() }} disabled={busy} /></label>
            </div>
            {!draftWithinCanvasLimits ? <p className="text-xs text-destructive">{copy.errors.draft}</p> : null}
            <label className="block text-sm font-medium">{copy.fit}<select className="mt-2 w-full rounded-md border bg-background px-3 py-2" value={fit} onChange={(event) => { setFit(event.target.value as FitMode); invalidateGeneration(false); revokeResult() }} disabled={busy}><option value="contain">{copy.contain}</option><option value="cover">{copy.cover}</option></select></label>
            <label className="block text-sm font-medium">{copy.globalDelay}<input className="mt-2 w-full rounded-md border bg-background px-3 py-2" type="number" min={MIN_DELAY_MS} max={10000} step={DELAY_STEP_MS} value={globalDelayMs} onChange={(event) => { setGlobalDelayMs(Number(event.target.value)); invalidateGeneration(false); revokeResult() }} disabled={busy} /></label>
            <label className="block text-sm font-medium">{copy.loop}<select className="mt-2 w-full rounded-md border bg-background px-3 py-2" value={loopMode} onChange={(event) => { setLoopMode(event.target.value as LoopMode); invalidateGeneration(false); revokeResult() }} disabled={busy}><option value="once">{copy.once}</option><option value="forever">{copy.forever}</option><option value="repeat">{copy.repeats}</option></select></label>
            {loopMode === 'repeat' ? <label className="block text-sm font-medium">{copy.repeatCount}<input className="mt-2 w-full rounded-md border bg-background px-3 py-2" type="number" min={MIN_REPEAT} max={MAX_REPEAT} value={repeatCount} onChange={(event) => { setRepeatCount(Number(event.target.value)); invalidateGeneration(false); revokeResult() }} disabled={busy} /></label> : null}
            <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={transparent} onChange={(event) => { setTransparent(event.target.checked); invalidateGeneration(false); revokeResult() }} disabled={busy} />{copy.transparent}</label>
            {!transparent ? <label className="flex items-center justify-between gap-3 text-sm font-medium">{copy.background}<input type="color" value={background} onChange={(event) => { setBackground(event.target.value); invalidateGeneration(false); revokeResult() }} disabled={busy} /></label> : <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs leading-5">{copy.transparencyWarning}</p>}
            <p className="rounded-md border bg-muted/30 p-3 text-xs leading-5 text-muted-foreground">{copy.paletteWarning}</p>
            <Button className="w-full" disabled={busy || assets.length < MIN_FRAMES || !draftWithinCanvasLimits} onClick={generate}>{busy ? `${copy.generating}…` : result ? copy.retry : copy.generate}</Button>
            {busy ? <div aria-live="polite"><div className="flex justify-between text-sm"><span>{copy.progress}</span><span>{progress.completed}/{progress.total}</span></div><div className="mt-2 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${progress.total ? progress.completed / progress.total * 100 : 0}%` }} /></div><Button className="mt-3 w-full" variant="outline" onClick={cancel}>{copy.cancel}</Button></div> : null}
            {result ? <div className="space-y-3"><h3 className="font-medium">{copy.actual}</h3><img className="w-full rounded-md border" src={result.url} alt={copy.actual} /><Button className="w-full" variant="outline" onClick={download}>{copy.download}</Button></div> : null}
            {assets.length > 0 ? <Button className="w-full" variant="ghost" disabled={busy} onClick={reset}>{copy.reset}</Button> : null}
            <p className="text-xs leading-5 text-muted-foreground">{copy.privacy}</p>
            <p className="text-xs leading-5 text-muted-foreground">{MIN_FRAMES}–{MAX_FRAMES} · {MAX_SOURCE_BYTES / 1024 / 1024} MB · 1280 px · 1 MP/frame · 12 MP total</p>
          </CardContent>
        </Card>
      </div>

      <section className="mt-8 grid gap-6 md:grid-cols-2">
        <Card><CardContent className="p-5"><h2 className="text-xl font-semibold">{copy.howToTitle}</h2><ol className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">{copy.howTo.map((step, index) => <li key={step}>{index + 1}. {step}</li>)}</ol></CardContent></Card>
        <Card><CardContent className="p-5"><h2 className="text-xl font-semibold">{copy.faqTitle}</h2><div className="mt-4 space-y-4">{copy.faq.map(([question, answer]) => <div key={question}><h3 className="font-medium">{question}</h3><p className="mt-1 text-sm leading-6 text-muted-foreground">{answer}</p></div>)}</div></CardContent></Card>
      </section>
      <nav className="mt-6 flex flex-wrap justify-center gap-3 text-sm" aria-label={copy.related}>
        <a className="rounded-md border bg-card px-3 py-2 hover:border-primary" href="/kollazh-iz-foto">{locale === 'ru' ? 'Коллаж из фото' : 'Photo collage'}</a>
        <a className="rounded-md border bg-card px-3 py-2 hover:border-primary" href="/izmenit-razmer-foto">{locale === 'ru' ? 'Изменить размер' : 'Resize image'}</a>
        <a className="rounded-md border bg-card px-3 py-2 hover:border-primary" href="/szhat-izobrazhenie">{locale === 'ru' ? 'Сжать изображение' : 'Compress image'}</a>
      </nav>
    </div>
  )
}
